// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  ExpandableOverviewItem,
  Table as Consolidations,
  SvgIcon,
  ChooseTableRow,
  Button,
  Notification,
  NavLink
} from '../'

import { ConsolidationEdit } from './ConsolidationEdit'
import { StatementFile } from './StatementFile'
import { Accounts } from './Accounts'

import { ReadOnlyContext } from '../../context'

import {
  showAsCurrency,
  decodeBase64,
  roundToDecimals,
  getTotal
} from '../../utils'

const MUTATION_CONSOLIDATION = gql`
  mutation updateConsolidation($input: ConsolidationMutationInput!) {
    consolidationMutate(input: $input) {
      consolidation {
        id
        bankaccountRef
        amount
        statementExpiresAt
        creditapplication {
          id
        }
      }
    }
  }
`

const QUERY_WORKFLOWRUNS = gql`
  query($creditApplication: String!) {
    workflowruns(description_Icontains: $creditApplication, orderBy: "-created_at") {
      edges {
        node {
          id
          description
        }
      }
    }
  }
`


export default class CreditApplicationCreditReport extends React.Component {
  static propTypes = {
    creditReport: PropTypes.shape({
      id: PropTypes.string,
      createdAt: PropTypes.string
    }),
    creditApplicationId: PropTypes.string.isRequired,
    creditType: PropTypes.string,
    accounts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      creditreportsupplier: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string
      }),
      balance: PropTypes.string,
      paymentFreqDesc: PropTypes.string,
      instalment: PropTypes.string,
      overdue: PropTypes.number,
      consolidation: PropTypes.object,
    })).isRequired,
    consolidations:  PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      amount: PropTypes.number,
      statementExpiresAt: PropTypes.string,
      bankaccountRef: PropTypes.string,
      approvedBy: PropTypes.shape({
        id: PropTypes.string
      }),
      creditreportaccount: PropTypes.shape({
        id: PropTypes.string,
        creditreportsupplier: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string
        }),
        balance: PropTypes.string
      })
    })),
    open: PropTypes.bool.isRequired,
    selectedFile: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    accountSelectedToConsolidate: null,
    accountConsolidationInProcess: false,
    editConsolidation: null,
    linkedConsolidationFiles: {}
  }

  selectAccountForConsolidation = (account) => this.setState({ accountSelectedToConsolidate: account })
  clearAccountSelection = () => this.setState({ accountSelectedToConsolidate: null })

  consolidateSelectedAccount = (consolidationMutate, creditReportAccount) => {
    this.setState(
      { accountConsolidationInProcess: true },
      () => (
        consolidationMutate({
          variables: {
            input: {
              creditapplication: this.props.creditApplicationId,
              creditreportaccount: creditReportAccount
            }
          }
        })
        .then(() => (
          this.setState(
            { accountSelectedToConsolidate: null, accountConsolidationInProcess: false },
            this.props.refetchData
          )
        ))
        .catch(error => this.setState({ accountSelectedToConsolidate: null, accountConsolidationInProcess: false }))
      )
    )
  }

  updateConsolidation = (consolidationMutate, consolidation, amountToConsolidate, statementExpiresAt) => {
    let input = {
      id: consolidation
    }
    if (amountToConsolidate) {
      input.amount = amountToConsolidate
    }
    if (statementExpiresAt) {
      input.statementExpiresAt = statementExpiresAt
    }
    consolidationMutate({ variables: { input } })
    .then(() => this.setState({ editConsolidation: null }))
    .catch(error => this.setState({ editConsolidation: null }))
  }

  resetEdit = () => {
    this.setState({ editConsolidation: null })
  }

  showSummary = (identityType, creditReport, accounts, consolidations, accountsNotConsolidatedInFull) => {
    if (creditReport) {
      const accountsBalanceTotal = accountsNotConsolidatedInFull.length > 0
        ? showAsCurrency('R', roundToDecimals(getTotal(accountsNotConsolidatedInFull.map(account => parseFloat(account.balance))), 2))
        : null
      const consolidationsAmountTotal = consolidations.length > 0
        ? showAsCurrency('R', roundToDecimals(getTotal(consolidations.map(consolidation => parseFloat(consolidation.amount))), 2))
        : null

      return accounts.length > 0 && accountsBalanceTotal
        ? `Accounts balance: ${accountsBalanceTotal}${consolidationsAmountTotal ? `, Consolidations total: ${consolidationsAmountTotal}`: ''}`
        : 'No outstanding debt'
    } else {
      if (identityType === 'PASSPORT') {
        return 'Missing credit report (as expected, foreign ID)'
      }
      if (identityType === 'ZA_ID') {
        return 'Missing expected credit report'
      }
    }
  }

  getIconColour = (identityType, creditReport) => {
    if (creditReport) {
      return 'success'  // green
    }

    if (creditReport === null && identityType === 'ZA_ID') {
      return 'error'  // red
    }

    if (creditReport === null && identityType === 'PASSPORT') {
      return 'warning'  // orange
    }

    return null
  }

  render() {
    let { creditReport, creditType, identity, accounts, consolidations, open, onClick, className } = this.props

    let accountsWithOutstandingDebt = accounts ? accounts.filter(account => account.balance > 0) : []
    let accountsNotConsolidatedInFull = accountsWithOutstandingDebt.filter(account => (
      account.consolidation.edges.length === 0 ||
      account.consolidation.edges[0].node.amount < account.openBalance  // NOTE: ran into a case where consolidation amount was larger than account balance
    ))

    const identityType = identity.identityType
    const consolidationLoanAppliedFor = creditType === 'CONSOLIDATION'

    return (
      <ExpandableOverviewItem
        title='Credit report'
        summary={this.showSummary(identityType, creditReport, accounts, consolidations, accountsNotConsolidatedInFull)}
        iconSymbol={creditReport ? 'check' : 'warning'}
        iconColour={this.getIconColour(identityType, creditReport)}
        open={open}
        onClick={onClick}
        className={className}>
        {!creditReport
          ? <div className='slds-grid slds-m-left_small slds-m-bottom_large'>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='light'
                size='small'
                className='slds-m-right_small'
              />
              No credit report received.
              <Query query={QUERY_WORKFLOWRUNS} variables={{ creditApplication: decodeBase64(this.props.creditApplicationId) }}>
                {({ loading, error, data }) => {
                  if (loading || error) {
                    return null
                  }
                  if (data.workflowruns.edges.length === 0) {
                    return '(No workflow runs found)'
                  } else {
                    return (
                      <div className='slds-m-left_small'>
                        See the corresponding <NavLink to={`/os/operations/general/workflows/${data.workflowruns.edges[0].node.id}`}>workflow run</NavLink>.
                      </div>
                    )
                  }
                }}
              </Query>
            </div>
          : accountsWithOutstandingDebt.length === 0
            ? <Notification
                type='info'
                message={`Client has no outstanding debt.  Credit report as of ${moment(creditReport.createdAt).format('llll')}`}
                closeIcon={false}
              />
            : <React.Fragment>
                <div>
                  <div className='u-font-size-larger u-font-weight-bold slds-m-bottom_small'>
                    Accounts
                  </div>
                  {accountsNotConsolidatedInFull.length > 0
                    ? consolidationLoanAppliedFor
                        ? <ReadOnlyContext.Consumer>
                            {readOnly => (
                              <React.Fragment>
                                {!readOnly &&
                                  <div className='slds-text-color_weak slds-m-bottom_small'>
                                    Choose account to consolidate
                                  </div>
                                }
                                <ChooseTableRow>
                                  <Accounts
                                    accountsNotConsolidatedInFull={accountsNotConsolidatedInFull}
                                    onClick={account => (
                                      readOnly
                                        ? false
                                        : this.selectAccountForConsolidation(account)
                                    )}
                                  />
                                </ChooseTableRow>
                              </React.Fragment>
                            )}
                          </ReadOnlyContext.Consumer>
                        : <Accounts accountsNotConsolidatedInFull={accountsNotConsolidatedInFull} />
                    : <div className='slds-text-color_weak slds-m-bottom_small slds-grid'>
                        <SvgIcon
                          variant='utility'
                          symbol='info_alt'
                          colour='light'
                          size='x-small'
                          className='slds-m-right_x-small'
                        />
                        No accounts not already selected for consolidation.
                      </div>
                  }
                  {this.state.accountSelectedToConsolidate &&
                    <div className='slds-m-bottom_medium slds-grid'>
                      <div>
                        <SvgIcon
                          variant='utility'
                          symbol='info_alt'
                          colour='warning'
                          size='x-small'
                          className='slds-m-right_x-small'
                        />
                        Consolidate the selected account?
                      </div>
                      <div className='slds-m-left_medium'>
                        <Button
                          onClick={this.clearAccountSelection}
                          minWidth={90}
                          className='slds-m-right_x-small'>
                          No
                        </Button>
                        <Mutation mutation={MUTATION_CONSOLIDATION}>
                          {(consolidationMutate, { data }) => (
                            <Button
                              type={data && data.error ? 'destructive' : 'brand'}
                              minWidth={90}
                              disabled={this.state.accountConsolidationInProcess}
                              onClick={() => this.consolidateSelectedAccount(consolidationMutate, this.state.accountSelectedToConsolidate.id)}>
                              {this.state.accountConsolidationInProcess
                                ? "Creating consolidation record..."
                                : "Yes"
                              }
                            </Button>
                          )}
                        </Mutation>
                      </div>
                    </div>
                  }
                </div>
                {consolidationLoanAppliedFor &&
                  <div className='slds-m-bottom_large'>
                    <div className='u-font-size-larger u-font-weight-bold slds-m-bottom_small'>
                      Consolidations
                    </div>
                    {consolidations.length === 0
                      ? <div className='slds-grid slds-m-left_small slds-m-bottom_large'>
                          <SvgIcon
                            variant='utility'
                            symbol='info'
                            colour='light'
                            size='small'
                            className='slds-m-right_small'
                          />
                          No accounts consolidated.
                        </div>
                      : <ChooseTableRow>
                          <ReadOnlyContext.Consumer>
                            {readOnly => (
                              <Consolidations
                                columns={[
                                  'Account',
                                  'Amount',
                                  'Statement',
                                  'Expiry',
                                  // ''
                                ]}
                                rows={this.props.consolidations.map(consolidation => (
                                  <React.Fragment key={`consolidation_${consolidation.id}`}>
                                    <tr onClick={() => readOnly ? false : this.setState({ editConsolidation: consolidation.id })}>
                                      <td>{consolidation.creditreportaccount.creditreportsupplier && consolidation.creditreportaccount.creditreportsupplier.name}</td>
                                      <td>{showAsCurrency('R', consolidation.amount)}</td>
                                      <td>
                                        <StatementFile
                                          file={this.state.linkedConsolidationFiles[consolidation.id]}
                                          consolidation={consolidation.id}
                                          viewFile={file => this.props.changeSelectedFile(file)}
                                        />
                                      </td>
                                      <td>
                                        {consolidation.statementExpiresAt
                                          ? moment(consolidation.statementExpiresAt).format('ll')
                                          : 'Not specified'
                                        }
                                      </td>
                                    </tr>
                                    {this.state.editConsolidation === consolidation.id &&
                                      <ConsolidationEdit
                                        consolidation={consolidation.id}
                                        accountBalance={consolidation.creditreportaccount.balance}
                                        amount={consolidation.amount}
                                        statementExpiresAt={consolidation.statementExpiresAt}
                                        selectedStatementFile={this.props.selectedFile}
                                        bankAccountRef={consolidation.bankaccountRef}
                                        onCancelClick={this.resetEdit}
                                        onSaved={this.resetEdit}
                                        linkedConsolidationFiles={this.state.linkedConsolidationFiles}
                                        updateLinkedConsolidationFiles={(linkedConsolidationFiles) => (
                                          this.setState({ linkedConsolidationFiles })
                                        )}
                                      />
                                    }
                                  </React.Fragment>
                                ))}
                                objectRows={true}
                                noCellPadding={true}
                                className='slds-m-bottom_large'
                              />
                            )}
                          </ReadOnlyContext.Consumer>
                        </ChooseTableRow>
                    }
                  </div>
                }
              </React.Fragment>
        }
      </ExpandableOverviewItem>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/credit-application-credit-report/index.js